import React, { Fragment, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import { Tooltip } from 'react-tooltip';
import './group-calendar.scss';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Event } from '../models/event';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass, faClock, faVideo } from '@fortawesome/free-solid-svg-icons';
import { EventAction, EventActionType } from '../models/eventAction';
import { NotificationFlag } from '../../components/NotificationFlag/NotificationFlag';
import { CalendarToolbar } from './toolbar/calendar-toolbar';
import { NotificationFlagsCategories } from '../../models/NotificationFlags/NotificationFlagsCategories';

interface CalendarEventItem {
    eventId: number | string;
    eventType:
        | 'event'
        | 'groupEvent'
        | 'exCommEvent'
        | 'conference'
        | 'groupOrder';
    title: string;
    start: Date;
    end: Date;
    isEvent: boolean;
}
export interface GroupCalendarProps {
    events: Event[];
    currentDate: Date;
    setCurrentDate: (date: Date) => void;
    setDetailsEventActions: React.Dispatch<any>;
    setDetailsEventActionType: React.Dispatch<React.SetStateAction<string>>;
    setSelectedEvent: React.Dispatch<Event>;
    selectedEvent: Event;
    setShowDetailsModalEventAction: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    groupId: number;
    notificationFlagsOnPreviousPages: number;
    notificationFlagsOnNextPages: number;
    isInitializingCalendarPage: boolean;
}

export const GroupCalendar = (props: GroupCalendarProps) => {
    const localizer = momentLocalizer(moment);
    let currentItemId = 0;
    const [calendarItems, setCalendarItems] = useState<CalendarEventItem[]>();

    useEffect(() => {
        let items = [];
        for (let event of props.events) {
            items.push({
                eventId: event.id,
                eventType: event.type,
                title: event.name,
                start: event.dateStart,
                end: event.dateEnd,
                isEvent: true,
                colorSettings: event.colorSettings,
            });
            if (event.eventActions?.length > 0) {
                for (let action of event.eventActions) {
                    items.push({
                        eventId: event.id,
                        eventType: event.type,
                        title: action.name,
                        start: action.date,
                        end: action.date,
                        isEvent: false,
                        actionType: action.type,
                        colorSettings: event.colorSettings,
                    });
                }
            }
        }
        setCalendarItems(items);
    }, [props.events]);

    const mappingEventActions = (type: EventActionType, event: Event) => {
        if (type === EventActionType.Deadline) {
            props.setDetailsEventActionType('Deadlines');
            var deadlines = event?.eventActions?.filter(
                (x: EventAction) => x.type === EventActionType.Deadline
            );
            if (deadlines) {
                props.setDetailsEventActions(deadlines);
            }
        } else {
            props.setDetailsEventActionType('Reminders');
            var reminders = event?.eventActions?.filter(
                (x: EventAction) => x.type === EventActionType.Reminder
            );
            if (reminders) {
                props.setDetailsEventActions(reminders);
            }
        }
    };

    const getClassNameByEventType = (type: string) => {
        switch (type) {
            case 'conference':
                return 'conference';
            case 'event':
                return 'event';
            case 'exCommEvent':
                return 'ex-comm-event';
            case 'groupEvent':
                return 'group-event';
            case 'groupOrder':
                return 'group-order';

            default:
                return 'event';
        }
    };

    const MonthEvent = ({ event: item }: any) => {
        let isReminder = item.isEvent
            ? false
            : item.actionType == EventActionType.Reminder;
        let isDeadline = item.isEvent
            ? false
            : item.actionType == EventActionType.Deadline;
        currentItemId++;
        const event = props.events.find((f) => f.id === item.eventId);
        const isToday =
            moment(item.start).isSame(moment(), 'day') &&
            moment(item.end).isSame(moment(), 'day');
        return (
            <div
                id={'event-title' + currentItemId}
                data-tip
                data-for="event"
                style={{
                    color:
                        props.selectedEvent &&
                        props.selectedEvent.id == item.eventId
                            ? item.colorSettings?.activeColor
                            : item.colorSettings?.color,
                    backgroundColor:
                        props.selectedEvent &&
                        props.selectedEvent.id == item.eventId
                            ? item.colorSettings?.activeBackgroundColor
                            : item.colorSettings?.backgroundColor,
                    fontWeight: isToday ? 'bold' : 'normal',
                }}
                className={
                    'calendar-cell ' +
                    (item.colorSettings
                        ? ''
                        : getClassNameByEventType(item.eventType) +
                          (props.selectedEvent &&
                          props.selectedEvent.id == item.eventId
                              ? ' active'
                              : ''))
                }
                onClick={() => {
                    props.setSelectedEvent(event);
                }}>
                {isReminder ? (
                    <FontAwesomeIcon
                        onClick={() => {
                            mappingEventActions(
                                EventActionType.Reminder,
                                event
                            );
                            props.setShowDetailsModalEventAction(true);
                        }}
                        className="icon"
                        icon={faClock}
                    />
                ) : (
                    <></>
                )}
                {isDeadline ? (
                    <FontAwesomeIcon
                        onClick={() => {
                            mappingEventActions(
                                EventActionType.Deadline,
                                event
                            );
                            props.setShowDetailsModalEventAction(true);
                        }}
                        className="icon"
                        icon={faHourglass}
                    />
                ) : (
                    <></>
                )}
                {item.eventType === 'conference' ? (
                    <FontAwesomeIcon className="icon" icon={faVideo} />
                ) : (
                    <></>
                )}
                <div
                    className="event-title"
                    onClick={() => {
                        event?.onClick && event.onClick();
                    }}>
                    {item.title}
                </div>
                {!isReminder && !isDeadline && event ? (
                    <NotificationFlag
                        isInitializing={props.isInitializingCalendarPage}
                        eventCode={`${NotificationFlagsCategories.PmgCode}.${props.groupId}.${NotificationFlagsCategories.CalendarCode}.${event.id}`}/>
                ) : (
                    []
                )}
                <Tooltip
                    anchorId={'event-title' + currentItemId}
                    className="tooltip-content"
                    id="event"
                    place="right">
                    <div>
                        {' '}
                        {isReminder ? (
                            <>
                                Reminder - {event.name}
                                <br />
                                {item.title}
                            </>
                        ) : isDeadline ? (
                            <>
                                Deadline - {event.name}
                                <br />
                                {item.title}
                            </>
                        ) : (
                            <>{item.title}</>
                        )}{' '}
                    </div>
                </Tooltip>
            </div>
        );
    };

    return (
        <Fragment>
            <Card className="mb-3 group-calendar">
                <CardBody>
                    <Calendar
                        showAllEvents={true}
                        tooltipAccessor={null}
                        localizer={localizer}
                        events={calendarItems}
                        startAccessor="start"
                        endAccessor="end"
                        defaultView={'month'}
                        views={{ month: true }}
                        components={{
                            event: MonthEvent,
                            toolbar: (insideProps) => (
                                <CalendarToolbar
                                    notificationFlagsOnPreviousPages={
                                        props.notificationFlagsOnPreviousPages
                                    }
                                    notificationFlagsOnNextPages={
                                        props.notificationFlagsOnNextPages
                                    }
                                    {...insideProps}
                                />
                            ),
                        }}
                        onNavigate={(data) => {
                            props.setCurrentDate(data);
                        }}
                        date={props.currentDate}
                    />
                </CardBody>
            </Card>
        </Fragment>
    );
};
